import React from 'react';
import classNames from 'classnames';
import { List } from 'immutable';
import i18next from 'i18next';
import { useSelector } from 'react-redux';

import { Paragraph, View, Block, Node } from '@uncinc/react-drupal-core';
import { Container, Image } from '@uncinc/uncinc-react-kitchen-sink';
import {
  Breadcrumbs,
  TitleHeaderBlock,
  InfoHeading,
  Experts,
  WysiwygField,
} from '../../../../components';

import {
  CONTENT_TYPE_NEWS,
  BLOCK_ID_GROUPED_CONTENT_DETAIL_CTA,
  GROUPED_CONTENT_TYPES,
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_OPINION,
  CONTENT_TYPE_INTERVIEW,
} from '../../../../config/constants';

const ctaRenderContentTypes = [
  'news',
  'article',
  'download',
  'opinion',
];

import './style.scss';

const ALLOWED_INFO_HEADING_TYPES = Object.keys(GROUPED_CONTENT_TYPES);

const GroupedContentFull = ({ entity, className, direction }) => {
  const nid = entity.getIn(['nid', 0, 'value']);
  const type = entity.getIn(['type', 0, 'target_id']);
  const relatedContent = entity.getIn(['field_related_content'], List());
  const themes = entity.getIn(['field_theme'], List());
  const themeIds = themes.map((theme) => theme.get('target_id'));
  const firstThemeName = themes.getIn([0, 'entity', 'name', 0, 'value'], null);
  const experts = entity.getIn(['field_experts'], List());
  const dossiers = entity.getIn(['field_dossiers'], List());
  const author = entity.getIn(['field_author', 0, 'value']);
  const authorImage = entity.getIn(['field_author_image', 0, 'styles', 'thumbnail']);
  const authorPosition = entity.getIn(['field_author_job_title', 0, 'value'], '');
  const relatedViewEntities = useSelector((state) => state.getIn(['view', 'news', 'recent', 'entities'], List()));
  const classes = classNames(className, [
    'GroupedContentFull',
    direction === 'right' ? 'reversed' : null,
    (relatedViewEntities.size > 0 || relatedContent.size > 0) && 'display-related-block',
  ]);

  const TYPES_WITH_RELATED_CONTENT = [
    CONTENT_TYPE_NEWS,
    CONTENT_TYPE_ARTICLE,
    CONTENT_TYPE_OPINION,
    CONTENT_TYPE_INTERVIEW,
  ];

  /**
   * Determine if related items should be shown for currently showed content
   * @param {string} type - The content type of the currently showed content
   * @returns {Boolean} - If related items should be shown for showed content
   */
  const contentTypeShouldShowRelatedItems = (type) => {
    return TYPES_WITH_RELATED_CONTENT.indexOf(type) > -1;
  };

  /**
   * Determine the title for the related items button
   * @param {string} type - The content type of the currently showed content
   * @returns {String} - The title of the button
   */
  const relatedItemsButtonTitle = (type) => {
    let buttonTitle = '';
    switch (type) {
      case CONTENT_TYPE_ARTICLE:
        buttonTitle = i18next.t('more_articles');
        break;
      case CONTENT_TYPE_OPINION:
        buttonTitle = i18next.t('more_opinions');
        break;
      case CONTENT_TYPE_INTERVIEW:
        buttonTitle = i18next.t('read_more');
        break;
      case CONTENT_TYPE_NEWS:
      default:
        buttonTitle = i18next.t('more_news');
        break;
    }

    return buttonTitle;
  };

  /**
   * Determine the root url the related items button should link to
   * @param {string} type - The content type of the currently showed content
   * @returns {String} - The title of the button
   */
  const relatedItemsurlRoot = (type) => {
    let urlRoot = '';
    switch (type) {
      case CONTENT_TYPE_ARTICLE:
        urlRoot = 'artikelen';
        break;
      case CONTENT_TYPE_OPINION:
        urlRoot = 'opinies';
        break;
      case CONTENT_TYPE_INTERVIEW:
        urlRoot = 'interviews';
        break;
      case CONTENT_TYPE_NEWS:
      default:
        urlRoot = 'nieuws';
        break;
    }

    return urlRoot;
  };

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Container small>

        {/* Breadcrumbs */}
        <Breadcrumbs includeCurrent />

        <div className="GroupedContentFull__content">
          <div className="GroupedContentFull__contentWrapper">

            {/* Info Heading */}
            {ALLOWED_INFO_HEADING_TYPES.includes(type) && (
              <InfoHeading entity={entity} />
            )}

            <h1>{entity.getIn(['title', 0, 'value'])}</h1>
            <div className="GroupedContentFull__intro">
              {entity.getIn(['body', 0, 'processed']) ? (
                <WysiwygField
                  property="schema:text"
                  className="node--page--body font-size--body--large"
                  html={entity.getIn(['body', 0, 'processed'], '')}
                />
              ) : null}
            </div>
          </div>
          {type === 'interview' && (
            <div className="GroupedContentFull__author">
              <div className="GroupedContentFull__author__image">
                <Image
                  alt={author}
                  lazyload={false}
                  src={authorImage} />
              </div>
              <div className="GroupedContentFull__author__details">
                <div className="GroupedContentFull__author__details__name">
                  {author}
                </div>
                {authorPosition && (
                  <div>
                    {authorPosition}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Container>

      {entity.get('field_paragraphs') ? (
        <div className="node--page--paragraphs-wrapper">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.getIn(['id', 0, 'value']);
            const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
            return (
              <Paragraph
                key={`paragraph:${id}:${language}`}
                id={id}
                entity={paragraph.get('entity')}
                mode="small"
              />
            );
          })}
        </div>
      ) : null}

      {experts.size > 0 && <Experts experts={experts.slice(0, 3)} />}

      {dossiers.size > 0 && (
        <TitleHeaderBlock
          title="Dossiers"
          linkText="Meer dossiers"
          linkUrl="/nl/dossiers"
        >
          <div className="node--page--dossiers-wrapper">
            {dossiers.map((dossier) => {
              const id = dossier.getIn(['id', 0, 'value']);
              const language = dossier.getIn(['entity', 'langcode', 0, 'value']);
              return (
                <Node
                  key={`dossier:${id}:${language}`}
                  id={id}
                  entity={dossier.get('entity')}
                  viewMode="teaser"
                  isLarge
                />
              );
            })}
          </div>
        </TitleHeaderBlock>
      )}

      {/* Show related/recent items at the bottom */}
      {(contentTypeShouldShowRelatedItems(type) && firstThemeName !== null) && (
        <TitleHeaderBlock
          title={`Meer over ${firstThemeName}`}
          linkText={relatedItemsButtonTitle(type)}
          linkUrl={`/nl/${relatedItemsurlRoot(type)}?theme=${themes.getIn([0, 'target_id'])}`}
        >
          {/* View content that is being rendered. */}
          <View
            id="news"
            display="recent"
            userSelectedEntities={relatedContent}
            filters={{
              field_theme_target_id: themeIds.size > 0 ? [themeIds.get(0)] : 'all',
              field_type: type,
            }}
            args={[nid]}
          />
        </TitleHeaderBlock>
      )}

      {/* Render the CTA block */}
      {ctaRenderContentTypes.includes(type) && (
        <Block id={BLOCK_ID_GROUPED_CONTENT_DETAIL_CTA} />
      )}

    </article>
  );
};

export default GroupedContentFull;
